import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, X, Send, CheckCircle2, Phone, Mail, BadgePercent } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal, { EASE } from "@/components/Reveal";
import { BIZ, SERVICE_OPTIONS, DELIVERY_METHODS } from "@/constants/content";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyRow = () => ({ qty: 1, make_model: "", serial: "", problem: "", service: "Not sure yet" });

export default function IntakeForm() {
  const [office, setOffice] = useState({ office_name: "", contact_name: "", phone: "", email: "", office_address: "" });
  const [delivery, setDelivery] = useState(DELIVERY_METHODS[0]);
  const [items, setItems] = useState([emptyRow()]);
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const setOfficeField = (k) => (e) => setOffice((o) => ({ ...o, [k]: e.target.value }));
  const setRow = (i, k, v) => setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const addRow = () => {
    if (items.length >= 12) return;
    setItems((rows) => [...rows, emptyRow()]);
  };
  const removeRow = (i) => setItems((rows) => (rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows));

  const validate = () => {
    if (!office.office_name.trim()) return "Office name is required";
    if (!office.contact_name.trim()) return "Contact name is required";
    if (!office.phone.trim()) return "Phone number is required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(office.email)) return "A valid email is required";
    if (items.some((r) => !r.make_model.trim())) return "Every handpiece needs a make & model";
    if (items.some((r) => !r.problem.trim())) return "Tell us what's wrong with each handpiece";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...office, delivery: delivery, delivery_method: delivery, items, notes, website: "" }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Something went wrong — please call us instead.");
      }
      const data = await res.json();
      setDone(data.reference);
      trackEvent("generate_lead", {
        method: "intake_form",
        delivery: delivery,
        handpieces: items.length,
        currency: "USD",
      });
      toast.success(`Intake ${data.reference} received!`);
    } catch (e2) {
      toast.error(e2.message || "Network error — please call or text (716) 256-6630.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setOffice({ office_name: "", contact_name: "", phone: "", email: "", office_address: "" });
    setItems([emptyRow()]);
    setNotes("");
    setDelivery(DELIVERY_METHODS[0]);
    setDone(null);
  };

  return (
    <section id="intake" className="relative overflow-hidden bg-brandsky-soft/60 py-20 sm:py-28" data-testid="intake-section">
      <div className="blueprint pointer-events-none absolute inset-0 opacity-50" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto mb-4 w-fit">
            <span className="inline-flex items-center gap-2 rounded-full bg-brandpink px-4 py-1.5 text-[11.5px] font-extrabold uppercase tracking-wider text-white shadow-lg">
              <BadgePercent className="h-3.5 w-3.5" /> 15% off your first order
            </span>
          </div>
          <h2 className="font-display mx-auto max-w-3xl text-center text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.05]">
            Handpiece repair intake
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-[15.5px] leading-relaxed text-slate-600">
            Document every handpiece you're sending — we'll know exactly what to expect before it arrives. Free
            estimates, always. To claim your <strong className="font-semibold text-ink">15% first-order discount</strong>,
            write a note in the form below.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="space-y-4">
              <div className="rounded-[22px] border border-ink/10 bg-white p-6 shadow-sm">
                <p className="eyebrow text-brandsky-deep">What happens next</p>
                <ol className="mt-4 space-y-4">
                  {[
                    "You send the list — every handpiece, its problem, its serial.",
                    "We call or email back with a free estimate and timing.",
                    "Van visit, free WNY pickup, or ship in — your choice.",
                  ].map((t, i) => (
                    <li key={t} className="flex gap-3.5">
                      <span className="font-mono mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brandsky-soft text-[12px] font-bold text-brandsky-deep">
                        {i + 1}
                      </span>
                      <span className="text-[14px] leading-relaxed text-slate-600">{t}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-[22px] border border-ink/10 bg-white p-6 shadow-sm" data-testid="intake-contact-card">
                <p className="eyebrow text-brandsky-deep">Rather talk it through?</p>
                <div className="mt-4 space-y-3">
                  {[BIZ.jacob, BIZ.jonah].map((p, i) => (
                    <a
                      key={p.name}
                      href={p.tel}
                      data-testid={`intake-call-${i === 0 ? "jacob" : "jonah"}-button`}
                      className="btn-lift flex items-center justify-between rounded-xl border border-ink/10 bg-paper px-4 py-3 hover:border-brandsky"
                    >
                      <span>
                        <span className="block text-[13.5px] font-bold text-ink">{p.name}</span>
                        <span className="block text-[11.5px] text-slate-500">{p.role} · call or text</span>
                      </span>
                      <span className="flex items-center gap-2 font-mono text-[13.5px] font-bold text-ink">
                        <Phone className="h-4 w-4 text-brandsky-deep" />
                        {p.phone}
                      </span>
                    </a>
                  ))}
                  <a
                    href={`mailto:${BIZ.email}`}
                    data-testid="intake-email-button"
                    className="btn-lift flex items-center justify-between rounded-xl border border-ink/10 bg-paper px-4 py-3 hover:border-brandsky"
                  >
                    <span className="text-[13.5px] font-bold text-ink">Email us</span>
                    <span className="flex items-center gap-2 text-[13px] font-semibold text-slate-600">
                      <Mail className="h-4 w-4 text-brandsky-deep" />
                      {BIZ.email}
                    </span>
                  </a>
                </div>
              </div>

              <div className="rounded-[22px] border border-brandleaf/30 bg-brandleaf-soft p-5">
                <p className="text-[13.5px] leading-relaxed text-ink/80">
                  <strong className="font-bold">In Buffalo, Erie or Rochester?</strong> Skip the mail entirely — the
                  van comes to you and most repairs are done in about an hour.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08} className="lg:col-span-8">
            <div className="overflow-hidden rounded-[24px] border border-ink/10 bg-white shadow-[0_20px_60px_rgba(22,50,79,0.12)]">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="flex min-h-[560px] flex-col items-center justify-center p-10 text-center"
                    data-testid="intake-success"
                  >
                    <span className="rounded-full bg-brandleaf-soft p-5">
                      <CheckCircle2 className="h-10 w-10 text-brandleaf-deep" />
                    </span>
                    <h3 className="font-display mt-6 text-2xl font-extrabold text-ink">Intake received!</h3>
                    <p className="mt-2 text-[14.5px] text-slate-600">
                      Your reference:{" "}
                      <span className="font-mono font-bold text-ink" data-testid="intake-reference">
                        {done}
                      </span>
                    </p>
                    <p className="mt-4 max-w-md text-[14px] leading-relaxed text-slate-500">
                      We'll call or email shortly to confirm details and timing. Questions in the meantime? Call or
                      text (716) 256-6630.
                    </p>
                    <button
                      onClick={reset}
                      data-testid="intake-reset-button"
                      className="mt-7 rounded-full border border-ink/15 px-6 py-3 text-[13.5px] font-bold text-ink hover:border-ink/40"
                    >
                      Submit another intake
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={submit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    className="p-6 sm:p-9"
                    data-testid="intake-form"
                  >
                    <p className="eyebrow text-brandsky-deep">1 · Office details</p>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <input className="field" placeholder="Office name *" value={office.office_name} onChange={setOfficeField("office_name")} data-testid="intake-input-office-name" />
                      <input className="field" placeholder="Contact name *" value={office.contact_name} onChange={setOfficeField("contact_name")} data-testid="intake-input-contact-name" />
                      <input className="field" placeholder="Phone *" type="tel" value={office.phone} onChange={setOfficeField("phone")} data-testid="intake-input-phone" />
                      <input className="field" placeholder="Email *" type="email" value={office.email} onChange={setOfficeField("email")} data-testid="intake-input-email" />
                      <input className="field sm:col-span-2" placeholder="Office address (for pickup or van visits)" value={office.office_address} onChange={setOfficeField("office_address")} data-testid="intake-input-address" />
                    </div>

                    <p className="eyebrow mt-8 text-brandsky-deep">2 · How do we get them?</p>
                    <div className="mt-4 grid gap-2.5 sm:grid-cols-3" role="radiogroup" aria-label="Delivery method">
                      {DELIVERY_METHODS.map((m) => (
                        <button
                          type="button"
                          key={m}
                          onClick={() => setDelivery(m)}
                          data-testid={`intake-delivery-${m.toLowerCase().split(" ")[0].replace(/[^a-z]/g, "")}`}
                          className={`rounded-xl border px-4 py-3 text-left text-[13px] font-semibold transition-all ${
                            delivery === m ? "border-ink bg-ink text-white shadow-md" : "border-ink/15 bg-white text-ink hover:border-brandsky"
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>

                    <div className="mt-8 flex items-center justify-between">
                      <p className="eyebrow text-brandsky-deep">3 · The handpieces</p>
                      <span className="font-mono text-[12px] font-semibold text-slate-400" data-testid="intake-count">
                        {items.length} listed
                      </span>
                    </div>

                    <div className="mt-4 space-y-4">
                      {items.map((r, i) => (
                        <motion.div
                          key={i}
                          layout
                          initial={{ opacity: 0, y: 14 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.35, ease: EASE }}
                          className="rounded-2xl border border-ink/10 bg-paper p-4 sm:p-5"
                          data-testid={`intake-row-${i}`}
                        >
                          <div className="mb-3 flex items-center justify-between">
                            <span className="font-mono text-[12px] font-bold text-brandsky-deep">HANDPIECE #{i + 1}</span>
                            {items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRow(i)}
                                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-brandpink-soft hover:text-brandpink"
                                aria-label={`Remove handpiece ${i + 1}`}
                                data-testid={`intake-remove-${i}`}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid gap-3 sm:grid-cols-12">
                            <input
                              type="number"
                              min="1"
                              max="50"
                              value={r.qty}
                              onChange={(e) => setRow(i, "qty", parseInt(e.target.value || "1", 10))}
                              className="field sm:col-span-2"
                              aria-label={`Quantity for handpiece ${i + 1}`}
                              data-testid={`intake-row-${i}-qty`}
                            />
                            <input
                              className="field sm:col-span-6"
                              placeholder="Make & model * (e.g. KaVo 649B)"
                              value={r.make_model}
                              onChange={(e) => setRow(i, "make_model", e.target.value)}
                              data-testid={`intake-row-${i}-model`}
                            />
                            <input
                              className="field sm:col-span-4"
                              placeholder="Serial #"
                              value={r.serial}
                              onChange={(e) => setRow(i, "serial", e.target.value)}
                              data-testid={`intake-row-${i}-serial`}
                            />
                            <select
                              className="field sm:col-span-4"
                              value={r.service}
                              onChange={(e) => setRow(i, "service", e.target.value)}
                              aria-label={`Service needed for handpiece ${i + 1}`}
                              data-testid={`intake-row-${i}-service`}
                            >
                              {SERVICE_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                            <input
                              className="field sm:col-span-8"
                              placeholder="Problem — what is wrong with it? *"
                              value={r.problem}
                              onChange={(e) => setRow(i, "problem", e.target.value)}
                              data-testid={`intake-row-${i}-problem`}
                            />
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={addRow}
                      data-testid="intake-add-handpiece-button"
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brandsky/50 bg-white/60 px-4 py-3.5 text-[13.5px] font-bold text-brandsky-deep transition-all hover:border-brandsky hover:bg-brandsky-soft"
                    >
                      <Plus className="h-4 w-4" /> Add another handpiece
                    </button>

                    <p className="eyebrow mt-8 text-brandsky-deep">4 · Notes</p>
                    <textarea
                      className="field mt-4 min-h-[96px] resize-y"
                      placeholder="Anything else — and to claim your 15% first-order discount, write a note (e.g. “15% off first order”)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      data-testid="intake-input-notes"
                    />

                    <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" onChange={() => {}} />

                    <div className="mt-7 flex flex-wrap items-center justify-between gap-4 border-t border-ink/10 pt-6">
                      <p className="text-[12.5px] text-slate-500" data-testid="intake-summary">
                        {items.length} handpiece{items.length > 1 ? "s" : ""} ready · free estimate to follow
                      </p>
                      <Button
                        type="submit"
                        disabled={sending}
                        data-testid="intake-submit-button"
                        className="btn-lift h-auto gap-2.5 rounded-full bg-ink px-8 py-4 text-[14.5px] font-bold text-white shadow-[0_14px_34px_rgba(22,50,79,0.3)] hover:bg-abyss disabled:opacity-60"
                      >
                        {sending ? (
                          <>
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            Sending…
                          </>
                        ) : (
                          <>
                            Send intake to JSL <Send className="h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}